import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { can, type StaffAction, type StaffModule } from "@ichegou/contracts";
import { api, call } from "./api.ts";
import { setDisplayTimeZone } from "./format.ts";

export const sessionQuery = queryOptions({
  queryKey: ["session"],
  queryFn: () => call(api.admin.session.get()),
  retry: false,
  staleTime: 5 * 60_000,
});
export type StaffSession = Awaited<ReturnType<typeof sessionQuery.queryFn & {}>>;

export function useSession() {
  return useSuspenseQuery(sessionQuery).data;
}

export function useCan(module: StaffModule, action: StaffAction = "ver") {
  return can(useSession().role.permissions, module, action);
}

/** Error codes that mean "go back to sign-in or 2FA", wherever they come from. */
export const SESSION_ERRORS = new Set(["unauthorized", "not_staff", "staff_inactive", "staff_domain", "staff_sign_in_method", "two_factor_setup_required", "two_factor_required"]);

const CITY_KEY = "ichegou.admin.city";
type CityCtx = { cityId: string; setCityId: (id: string) => void; cities: StaffSession["cities"] };
const CityContext = createContext<CityCtx | null>(null);

/** The city every screen is scoped to (and whose time zone dates are shown in); remembered per browser. */
export function CityProvider({ children }: { children: ReactNode }) {
  const { cities } = useSession();
  const [cityId, setCity] = useState(() => {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(CITY_KEY);
    } catch {}
    return cities.find((c) => c.id === saved)?.id ?? cities[0]?.id ?? "";
  });
  useEffect(() => {
    if (!cities.some((c) => c.id === cityId) && cities[0]) setCity(cities[0].id);
  }, [cities, cityId]);
  // Dates and times follow the selected city's zone. Set during render, so the screens below format with it.
  setDisplayTimeZone(cities.find((c) => c.id === cityId)?.timezone);
  const setCityId = (id: string) => {
    setCity(id);
    try {
      localStorage.setItem(CITY_KEY, id);
    } catch {}
  };
  return <CityContext.Provider value={{ cityId, setCityId, cities }}>{children}</CityContext.Provider>;
}

export function useCity() {
  const ctx = useContext(CityContext);
  if (!ctx) throw new Error("useCity outside CityProvider");
  return ctx;
}
